import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronsLeft, ChevronsRight, X } from "lucide-react";
import * as React from "react";
import { useEffect, useRef, useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/** Hover→appear, auto-dismiss after `dismissMs`, suppressed until the mouse
 *  leaves and re-enters. Keeps Radix Tooltip's keyboard focus behavior intact;
 *  only auto-closes when the user is hovering, so screen-reader users still
 *  get the persistent label. */
const TOOLTIP_DISMISS_MS = 2000;

function useAutoDismissTooltip(): {
  open: boolean;
  setOpen: (v: boolean) => void;
} {
  const [open, setOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!open) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }
    timerRef.current = setTimeout(() => setOpen(false), TOOLTIP_DISMISS_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open]);
  return { open, setOpen };
}

/**
 * Slide-in entity drawer with a single animation continuum from "quick view"
 * to "full page". Clicking the expand chevron in the header animates the
 * drawer leftward to fill the viewport minus the sidebar (read off the CSS
 * variable `--sidebar-w`, which Sidebar.tsx writes whenever it collapses).
 *
 * Two modes:
 * - quick: `w-[480px]` on desktop, full-width on mobile, dimmed backdrop, esc/
 *   backdrop click closes the drawer.
 * - expanded: `w-[calc(100vw-var(--sidebar-w))]`, no backdrop, esc/backdrop are
 *   no-ops (the drawer IS the page now).
 *
 * The consumer owns URL state — pass `expanded` / `onExpandedChange` and
 * `open` / `onOpenChange` and wire them to router navigation.
 */

export interface EntityDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  /** Override the close behavior in expanded mode. Defaults to onOpenChange(false). */
  onClose?: () => void;
}

export function EntityDrawer({
  open,
  onOpenChange,
  expanded,
  onExpandedChange,
  title,
  subtitle,
  children,
  onClose,
}: EntityDrawerProps) {
  return (
    <TooltipProvider delayDuration={250}>
      <DialogPrimitive.Root
        open={open}
        onOpenChange={(v) => {
          // In expanded mode, esc and backdrop clicks are no-ops — the drawer
          // is acting as a page. Consumer-driven close only.
          if (expanded && !v) return;
          onOpenChange(v);
        }}
        // Non-modal in BOTH modes so the sidebar nav remains clickable while
        // the drawer is open (Radix's modal=true marks the rest of the doc
        // inert, which would also block the sidebar). Quick mode kept the
        // dimmed backdrop; expanded mode's drawer geometry naturally clears
        // the sidebar area.
        modal={false}
      >
        <DialogPrimitive.Portal>
          {/* Backdrop — present in quick mode only; expanded mode's drawer
              IS the page, so we don't dim anything. In quick mode the
              backdrop intentionally clears the sidebar (`left-[var(...)]`)
              so the user can click sidebar links without dismissing the
              drawer through the overlay. */}
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-y-0 right-0 z-40 transition-[background-color,opacity] duration-200",
              expanded
                ? "pointer-events-none bg-transparent left-0"
                : "bg-foreground/40 left-[var(--sidebar-w,15rem)]",
            )}
          />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            // Trap esc/escape-via-outside-click in expanded mode: the drawer
            // header carries the close affordance. Without these, non-modal
            // dialogs let any outside click close the dialog — but we want
            // sidebar clicks to navigate, not just close.
            onPointerDownOutside={(e) => {
              if (expanded) e.preventDefault();
            }}
            onInteractOutside={(e) => {
              if (expanded) e.preventDefault();
            }}
            // Never close on focus alone. A non-modal Radix dialog treats a
            // focusin anywhere outside as a dismissal, and every menu/popover
            // returns focus to its trigger as it closes — so a drawer opened
            // FROM a dropdown item was killed by the dropdown's own closing
            // focus, one frame after it appeared. Dismissal here is a
            // deliberate pointer gesture (backdrop click) or the X, not a
            // focus move.
            onFocusOutside={(e) => e.preventDefault()}
            className={cn(
              "fixed inset-y-0 right-0 z-50 flex flex-col bg-background shadow-2xl",
              "transition-[width,transform] duration-[280ms] ease-out",
              "data-[state=closed]:translate-x-full data-[state=open]:translate-x-0",
              expanded
                ? "w-[calc(100vw-var(--sidebar-w,15rem))] border-l-0"
                : "w-full max-w-[480px] border-l",
            )}
          >
            <DrawerHeader
              expanded={expanded}
              onExpandedChange={onExpandedChange}
              title={title}
              subtitle={subtitle}
              // The X button always closes; the optional onClose lets a host
              // do additional cleanup (route nav, URL state) before
              // ``onOpenChange(false)`` fires. Without this fallback the X
              // was a no-op when the consumer didn't pass onClose.
              onClose={onClose ?? (() => onOpenChange(false))}
            />
            <div className="flex-1 overflow-y-auto">{children}</div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </TooltipProvider>
  );
}

function DrawerHeader({
  expanded,
  onExpandedChange,
  title,
  subtitle,
  onClose,
}: {
  expanded: boolean;
  onExpandedChange: (v: boolean) => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  onClose?: () => void;
}) {
  // Controlled Radix Tooltip so we can auto-dismiss after a short window even
  // while the user keeps hovering — the chevron's intent is obvious once
  // you've seen the label, and a persistent tooltip distracts from the
  // drawer content.
  const tip = useAutoDismissTooltip();
  return (
    <header className="sticky top-0 z-10 flex h-14 items-center gap-3 border-b border-border bg-background px-3">
      <Tooltip open={tip.open} onOpenChange={tip.setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => onExpandedChange(!expanded)}
            aria-label={expanded ? "Collapse to quick view" : "Open full page"}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {expanded ? (
              <ChevronsRight className="h-4 w-4" />
            ) : (
              <ChevronsLeft className="h-4 w-4" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {expanded ? "Quick view" : "Open full page"}
        </TooltipContent>
      </Tooltip>

      <div className="min-w-0 flex-1">
        <DialogPrimitive.Title className="truncate text-sm font-semibold leading-tight text-foreground">
          {title}
        </DialogPrimitive.Title>
        {subtitle ? (
          <div className="truncate text-xs text-muted-foreground">{subtitle}</div>
        ) : null}
      </div>

      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="h-4 w-4" />
      </button>
    </header>
  );
}
